import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatError } from "@/lib/api";
import { Lock, AlertTriangle } from "lucide-react";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Missing or invalid reset token.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (password !== confirm) return toast.error("Passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password reset. Please sign in.");
      nav("/login");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Reset failed. Try requesting a new link.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="mx-auto max-w-md px-6 py-24 text-center" data-testid="reset-password-bad-token">
        <AlertTriangle className="w-14 h-14 mx-auto text-destructive mb-6" strokeWidth={1.5} />
        <h1 className="font-display text-3xl">This link isn't valid.</h1>
        <p className="mt-3 text-primary/70">Request a new reset link and try again.</p>
        <Button asChild className="mt-8"><Link to="/forgot-password">Request a new link</Link></Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-6 py-24">
      <div className="label-caps text-accent mb-3">Set a new password</div>
      <h1 className="font-display text-4xl text-primary">Almost there.</h1>
      <p className="text-primary/70 mt-3 text-sm">Choose a password you'll remember. Minimum 8 characters.</p>

      <form onSubmit={submit} className="mt-10 space-y-5" data-testid="reset-password-form">
        <div>
          <Label>New password</Label>
          <div className="mt-2 relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary/40" />
            <Input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
                   className="rounded-none pl-10" data-testid="reset-password-new" />
          </div>
        </div>
        <div>
          <Label>Confirm new password</Label>
          <div className="mt-2 relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary/40" />
            <Input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                   className="rounded-none pl-10" data-testid="reset-password-confirm" />
          </div>
        </div>
        <Button type="submit" disabled={loading} className="w-full rounded-full h-11" data-testid="reset-password-submit">
          {loading ? "Resetting…" : "Reset password"}
        </Button>
      </form>
    </div>
  );
}
